import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import Tooltip from "../components/Tooltip";
import oauthConfig from "../config/oauthConfig";
import { useAuth } from "../context/AuthContext";
import AuthService from "../services/authService";
import "./LoginForm.scss";
import { isValidEmail } from "../utils/utils";
import UserService from "../services/userService";
import { useSearchParams } from "react-router-dom";

interface LoginFormProps {
  onSubmit: (username: string, password: string) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const mustBeAdmin = searchParams.get("adminMode");

  useEffect(() => {
    setIsButtonDisabled(username === "" || password === "");
  }, [username, password]);

  const mutation = useMutation(AuthService.login, {
    onSuccess: (data) => {
      login(data.token);
      onSubmit(username, password);
    },
    onError: (error: any) => {
      setError(t("The account is incorrect"));
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isForgotPassword) {
      UserService.forgotPassword(email)
        .then(() => {
          alert(t("A reset link has been sent to your email"));
          setIsForgotPassword(false);
        })
        .catch((error) => {
          console.error(error);
          alert(t("Failed to send reset link"));
        });
      return;
    }
    const username = (event.target as any).username.value;
    const password = (event.target as any).password.value;
    mutation.mutate({ username, password });
  };
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const isEmailValid = useMemo(() => {
    return isValidEmail(email);
  }, [email]);

  const forgotRender = () => {
    return (
      <form onSubmit={handleSubmit} className="mt-8 sm:w-[30rem]">
        <h3 className="title-popup mb-8 font-semibold leading-[2.5537rem]">
          {t("Forgot password")}
        </h3>
        <input
          type="text"
          id="email"
          name="email"
          placeholder={t("Email")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-[1.25rem] h-[3.125rem] w-full rounded-[0.625rem] border p-2"
        />
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => setIsForgotPassword(false)}
            className={`text-1 mb-6 mt-6 h-[3.125rem] w-full rounded-full bg-[#E9EAEB] p-2 font-semibold text-[#232323]`}
          >
            {t("Back")}
          </button>
          <button
            type="submit"
            disabled={!isEmailValid}
            className={`text-1 mb-6 mt-6 h-[3.125rem] w-full rounded-full p-2 font-semibold text-white ${
              !isEmailValid ? "bg-[#BFC5C9]" : "bg-[#232323]"
            }`}
          >
            {t("send the reset link")}
          </button>
        </div>
      </form>
    );
  };

  const signinRender = () => {
    return (
      <form onSubmit={handleSubmit} className="mt-8 sm:w-[30rem]">
        <h3 className="title-popup mb-8 font-semibold leading-[2.5537rem]">
          {t("Sign in to your account")} {mustBeAdmin && `(${t("Admin")})`}
        </h3>
        <input
          type="text"
          id="username"
          name="username"
          list="username"
          placeholder={t("Email or Username")}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="mb-[1.25rem] h-[3.125rem] w-full rounded-[0.625rem] border p-2"
        />

        <div className="relative w-full">
          <input
            type={showPassword ? "text" : "password"}
            id="password"
            name="password"
            placeholder={t("Password")}
            className="h-[3.125rem] w-full rounded-[0.625rem] border p-2 text-[0.875rem] leading-[1.1919rem]"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5"
          >
            <img
              src={`../assets/images/eye${showPassword ? "-slash" : ""}.svg`}
              alt=""
            />
          </button>
        </div>
        {error && (
          <div className="mt-2 text-[0.75rem] text-[#DA3838]">{error}</div>
        )}

        <button
          type="submit"
          disabled={isButtonDisabled}
          className={`text-1 mb-6 mt-6 h-[3.125rem] w-full rounded-full p-2 font-semibold text-white ${
            isButtonDisabled ? "bg-[#BFC5C9]" : "bg-[#232323]"
          }`}
        >
          {t("Log in")}
        </button>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="rememberMe"
              name="rememberMe"
              className="custom-checkbox"
            />
            <label
              htmlFor="rememberMe"
              className="ml-2 block text-[0.875rem] text-[#5E6D77]"
            >
              {t("Remember me")}
            </label>
          </div>
          <button
            type="button"
            onClick={() => setIsForgotPassword(true)}
            className="text-[0.875rem] font-normal leading-[1.1919rem] text-[#5E6D77]"
          >
            {t("Forgot Password?")}
          </button>
        </div>
        <div className="mb-6 flex items-center">
          <hr className="w-full" />
          <span className="px-[0.875rem] text-[0.875rem] leading-[1.1919rem] text-[#5E6D77]">
            {t("Or")}
          </span>
          <hr className="w-full" />
        </div>
        <div className="flex h-[3.125rem] justify-center gap-4">
          <SocialLoginBtns />
        </div>
      </form>
    );
  };

  return <>{isForgotPassword ? forgotRender() : signinRender()}</>;
};

export default LoginForm;

const SocialLoginBtns = () => {
  const { t } = useTranslation();
  const mutation = useMutation(
    (data: { provider: string; code: string }) =>
      AuthService.outboundLogin(data.provider, data.code),
    {
      onSuccess: (data) => {
        localStorage.setItem("token", data.token);
        console.log(data);
        // window.close();
        window.location.reload();
      },
      onError: (error: any) => {
        alert("fail: " + error.message);
        // window.close();
      },
      retry: false,
    },
  );

  const handleOAuthLogin = (provider: keyof typeof oauthConfig) => {
    const config = oauthConfig[provider];

    // Construct URL safely
    const queryParams = new URLSearchParams({
      redirect_uri: config.redirectUri,
      ...(config.responseType && { response_type: config.responseType }),
      ...(config.clientId && { client_id: config.clientId }),
      ...(config.scope && { scope: config.scope }),
      ...(config.state && { state: config.state }),
      ...(config.responseMode && { response_mode: config.responseMode }),
      ...(config.access_type && { access_type: config.access_type }),
    });

    const targetUrl = `${config.authUrl}?${queryParams.toString()}`;

    const authWindow = window.open(targetUrl, "_blank", "width=500,height=600");

    if (!authWindow) {
      console.error("Failed to open authentication window.");
      return;
    }
    const interval = setInterval(() => {
      if (!authWindow.closed) {
        return;
      }
      clearInterval(interval);

      // const url = authWindow.location.href;
      const search = authWindow?.location?.search;
      // alert("search: " + authWindow.location.search);

      if (!search) {
        return;
      }
      const code =
        new URLSearchParams(authWindow.location.search).get("code") ?? "";

      console.log(code);

      mutation
        .mutateAsync({ provider, code })
        .then(() => {
          console.log("success");
          authWindow.close();
        })
        .catch((error) => {
          console.log("error", error);
          authWindow.close();
        });

      // if (code) {
      //   AuthService.handleOAuthLogin(provider, code);
      // } else {
      //   console.error("Failed to retrieve authentication code.");
      // }
      // authWindow.close();
      // window.location.reload();
    }, 100);
  };
  const socials = ["kakao", "google", "naver", "apple"];
  const recently = useMemo(() => {
    return localStorage.getItem("recentlyLoginMethod") ?? "";
  }, []);
  console.log("recently", recently);
  const [showLoginMethod, setShowLoginMethod] = useState("");
  useEffect(() => {
    setShowLoginMethod(recently);
    const inv = setTimeout(() => {
      setShowLoginMethod("");
    }, 5000);
    return () => {
      clearTimeout(inv);
    };
  }, [recently]);
  return (
    <>
      {socials.map((social) => {
        return (
          <Tooltip
            isVisible={showLoginMethod === social}
            key={social}
            text={t(`Recent login`)}
          >
            <button
              type="button"
              onClick={() => {
                handleOAuthLogin(social);
                localStorage.setItem("recentlyLoginMethod", social);
              }}
              className="flex h-[3.125rem] w-[100%] flex-1 items-center justify-center rounded-[1.875rem] border text-center text-sm leading-5"
            >
              <img src={`../assets/images/${social}-logo.svg`} alt="" />
            </button>
          </Tooltip>
        );
      })}
    </>
  );
};
